import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  TextField,
  IconButton,
} from '@mui/material';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTheme } from '@mui/material';
import { PlateRegion, BaseEntity, Plate } from '../types';
import { TASK_TYPE_CONFIG, getTaskBgColor } from '../constants/taskConfig';
import PlateImage from './PlateImage';

// Constants
const THEME_COLOR = TASK_TYPE_CONFIG.plate.color;
const PLATE_IMAGE_WIDTH = 240;
const PLATE_IMAGE_HEIGHT = 72;

// Helper function for country flag
const getCountryFlag = (code: string): string => {
  if (code.startsWith('us-')) return '🇺🇸';
  if (code.startsWith('ca-')) return '🇨🇦';
  return '';
};

interface PlateCardProps {
  plate: Plate;
  imageBaseUrl: string;
  isSelected: boolean;
  editingTag: string;
  regions: PlateRegion[];
  datasets: BaseEntity[];
  flags: BaseEntity[];
  onToggleSelection: (plateId: string) => void;
  onTagChange: (plateId: string, newTag: string) => void;
  onFieldChange: (plateId: string, field: 'region' | 'dataset' | 'flags', value: string | string[]) => void;
  onImageClick: (plate: Plate) => void;
  hideSelection?: boolean;
  onHide?: (id: string) => void;
}

const PlateCard: React.FC<PlateCardProps> = ({
  plate,
  imageBaseUrl,
  isSelected,
  editingTag,
  regions,
  datasets,
  flags,
  onToggleSelection,
  onTagChange,
  onFieldChange,
  onImageClick,
  hideSelection = false,
  onHide,
}) => {
  const theme = useTheme();

  return (
    <Card
      elevation={isSelected ? 4 : 2}
      sx={{
        bgcolor: isSelected ? getTaskBgColor('plate', theme.palette.mode) : 'transparent',
        border: isSelected ? `2px solid ${THEME_COLOR}` : 'none',
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Selection checkbox and hide button */}
        {!hideSelection && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSelected}
                  onChange={() => onToggleSelection(plate.id)}
                  size="small"
                  sx={{ color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                />
              }
              label={<Typography variant="caption" color="text.secondary">Select</Typography>}
            />
            {onHide && (
              <IconButton
                size="small"
                onClick={() => onHide(plate.id)}
                title="Hide item"
                sx={{ ml: 'auto', color: 'text.secondary' }}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        {/* Plate Image */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            justifyContent: 'center',
            bgcolor: 'action.disabledBackground',
            borderRadius: 1,
            p: 1,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            '&:hover': { bgcolor: 'action.selected' },
          }}
          onClick={() => onImageClick(plate)}
          title="Click to edit alignment"
        >
          <PlateImage
            URL={`${imageBaseUrl}/${plate.id}`}
            corners={plate.corners}
            width={PLATE_IMAGE_WIDTH}
            height={PLATE_IMAGE_HEIGHT}
          />
        </Box>

        {/* Tag (editable) */}
        <TextField
          fullWidth
          size="small"
          label="Tag"
          value={editingTag}
          onChange={(e) => onTagChange(plate.id, e.target.value)}
          sx={{ mb: 1.5 }}
          inputProps={{ style: { fontFamily: 'monospace', fontWeight: 'bold' } }}
        />

        {/* Region (editable) */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={plate.region.id}
            onChange={(e) => onFieldChange(plate.id, 'region', e.target.value)}
            label="Region"
          >
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                {getCountryFlag(region.code)} {region.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Dataset (editable) */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Dataset</InputLabel>
          <Select
            value={plate.dataset.id}
            onChange={(e) => onFieldChange(plate.id, 'dataset', e.target.value)}
            label="Dataset"
          >
            {datasets.map((dataset) => (
              <MenuItem key={dataset.id} value={dataset.id}>
                {dataset.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Flags (editable) */}
        <FormControl fullWidth size="small">
          <InputLabel>Flags</InputLabel>
          <Select
            multiple
            value={plate.flags.map((f) => f.id)}
            onChange={(e) => onFieldChange(plate.id, 'flags', e.target.value as string[])}
            input={<OutlinedInput label="Flags" />}
            renderValue={(selected) =>
              selected
                .map((id) => flags.find((f) => f.id === id)?.label || id)
                .join(', ')
            }
          >
            {flags.map((flag) => (
              <MenuItem key={flag.id} value={flag.id}>
                {flag.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </CardContent>
    </Card>
  );
};

export default React.memo(PlateCard);
